import { ImojePayment } from '../models/imoje-payment.model';
import { PaymentMethod } from '../models/payment-method.model';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PaymentTypeEnum } from '../models/payment-type.model';
import { ImojeAction } from '../models/imoje-action.model';
import { ImojeWidgetParams } from '../models/imoje-widget-params.model';
import { tap } from 'rxjs/operators';
import { PersonalData } from './payment.service';

@Injectable({
  providedIn: 'root'
})
export class ImojePaymentService {
  private static MULTIPAYOUT = 'multipayout';

  private readonly apiPath = `${environment.apiPath}/payments/imoje`;

  constructor(private http: HttpClient) {}

  private static getMultipayoutIndex(key: string) {
    return parseInt(key.split('[')[1].split(']')[0], 10);
  }

  public createImojePayment(
    payByLinkToken: string,
    paymentMethod: PaymentMethod,
    forename: string,
    surname: string,
    email: string,
    paymentType: PaymentTypeEnum = PaymentTypeEnum.LIABILITY
  ): Observable<ImojePayment> {
    let body: any = {
      payByLinkToken,
      paymentMethod,
      email
    };

    if (forename) {
      body = { ...body, forename, surname };
    }

    const url = this.apiPath + (paymentType === PaymentTypeEnum.BALANCE ? '/balance' : '');
    return this.http.post<ImojePayment>(url, body);
  }

  public createImojeTransaction(
    payByLinkToken: string,
    paymentMethod: PaymentMethod,
    forename: string,
    surname: string,
    email: string,
    paymentType: PaymentTypeEnum = PaymentTypeEnum.LIABILITY,
    paymentMethodCode?: string
  ): Observable<ImojeAction> {
    let body: any = {
      payByLinkToken,
      paymentMethod,
      email
    };

    if (surname) {
      body = { ...body, forename, surname };
    }

    body = { ...body, paymentType };
    if (paymentMethod === PaymentMethod.TRANSFER) {
      body = { ...body, paymentMethodCode };
    }

    if (paymentMethod === PaymentMethod.SIMP && environment.mockWiretransfer) {
      return of({ ban: 'mock' });
    } else {
      return this.http.post<ImojeAction>(`${this.apiPath}/transaction-call`, body);
    }
  }

  submitImojePayment(imojePayment: ImojePayment) {
    this.submitPaymentForm(imojePayment.apiUrl, imojePayment.paymentRequestParams);
  }

  submitImojeAction(imojeAction: ImojeAction) {
    if (imojeAction.method.toUpperCase() === 'GET') {
      window.location.href = `${imojeAction.url}?${imojeAction.contentBodyRaw}`;
    } else {
      this.submitPaymentForm(imojeAction.url, imojeAction.postActionParams);
    }
  }

  startPaymentAgain(token: string): Observable<any> {
    return this.http.put(`${this.apiPath}/${token}/cancel`, null);
  }

  private submitPaymentForm(url: string, params: { [p: string]: string }) {
    const form = document.createElement('form');
    const formParams = this.sortPaymentParam(Object.entries(params));

    form.setAttribute('method', 'POST');
    form.setAttribute('action', url);

    for (const [key, value] of formParams) {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', value + '');
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  private sortPaymentParam(paramKeys: Array<[string, string]>) {
    paramKeys.sort((prevEntry, nextEntry) => {
      const [prevKey] = prevEntry;
      const [nextKey] = nextEntry;
      if (prevKey.includes(ImojePaymentService.MULTIPAYOUT) && nextKey.includes(ImojePaymentService.MULTIPAYOUT)) {
        const prevIndex = ImojePaymentService.getMultipayoutIndex(prevKey);
        const nextIndex = ImojePaymentService.getMultipayoutIndex(nextKey);
        if (prevIndex === nextIndex) {
          return prevKey < nextKey ? -1 : 1;
        }
        return prevIndex - nextIndex;
      }
      return prevKey < nextKey ? -1 : 1;
    });
    return paramKeys;
  }

  createPaymentCardWidget(
    token: string,
    personalData: PersonalData,
    paymentType: PaymentTypeEnum = PaymentTypeEnum.LIABILITY
  ) {
    const { email, name, surname } = personalData;
    const body = { email, forename: name, surname, paymentType };

    return this.http
      .post<ImojeWidgetParams>(`${this.apiPath}/${token}/card-payment`, body)
      .pipe(tap(widgetParams => this.appendImojeScript(widgetParams)));
  }

  public appendImojeScript(widgetParams: ImojeWidgetParams): void {
    const script = document.createElement('script');
    script.src = widgetParams.widgetUrl;
    script.id = 'imoje-widget__script';
    script.setAttribute('data-inline', 'true');
    for (const [key, value] of Object.entries(widgetParams.params)) {
      script.setAttribute(key, value);
    }
    document.getElementsByTagName('head')[0].appendChild(script);
  }
}
